import { useState } from "react";
import { AlertTriangle, Save } from "lucide-react";
import type { CalendarException, ScenarioCalendarOverride } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { cn } from "@/lib/utils";
import { ShiftsEditor } from "@/features/calendar/ShiftsEditor";
import { ExceptionsList } from "@/features/calendar/ExceptionsList";
import { fromDraft, toDraft } from "@/features/calendar/calendar-draft";
import type { ShiftForm } from "@/features/calendar/time-helpers";
import { useSetScenarioCalendar } from "./hooks";

type Mode = ScenarioCalendarOverride["mode"];

interface Props {
  scenarioId: string;
  /** Linha base do cenário; null ⇒ "linha nova" (sem opção Herdar). */
  lineId: string | null;
  initial: ScenarioCalendarOverride | null;
  onDone: () => void;
}

// Seletor de calendário do cenário: Herdar (da linha base) / 24 horas /
// Cópia editável própria. A cópia REUSA os editores do expediente da linha, nunca duplica.
export function ScenarioCalendarSelector({ scenarioId, lineId, initial, onDone }: Props) {
  const defaultMode: Mode = initial?.mode ?? (lineId ? "inherit" : "always_on");
  const [mode, setMode] = useState<Mode>(defaultMode);
  const seed = toDraft(initial?.custom ?? null);
  const [shifts, setShifts] = useState<ShiftForm[]>(seed.shifts);
  const [exceptions, setExceptions] = useState<CalendarException[]>(seed.exceptions);
  const setCalendar = useSetScenarioCalendar(scenarioId);

  const custom = fromDraft({ shifts, exceptions });
  const customInvalid = mode === "custom" && (!custom.calendar || !!custom.error);

  const options: Array<{ value: Mode; label: string; hint: string }> = [
    ...(lineId
      ? [
          {
            value: "inherit" as const,
            label: "Herdar da linha",
            hint: "Usa o expediente da linha base.",
          },
        ]
      : []),
    { value: "always_on", label: "24 horas", hint: "Opera sem parar (padrão de linha nova)." },
    {
      value: "custom",
      label: "Cópia própria",
      hint: "Edite turnos só neste cenário — não altera a linha.",
    },
  ];

  async function save() {
    if (customInvalid) return;
    const override: ScenarioCalendarOverride =
      mode === "custom" && custom.calendar ? { mode: "custom", custom: custom.calendar } : { mode };
    await setCalendar.mutateAsync(override);
    toast.success("Calendário do cenário atualizado.");
    onDone();
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <fieldset className="space-y-2">
          <Label className="text-[13px] font-medium">Calendário deste cenário</Label>
          {options.map((opt) => (
            <label
              key={opt.value}
              className={cn(
                "grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-2 rounded-md border px-3 py-2",
                mode === opt.value ? "border-brand-600 bg-brand-50" : "border-border-subtle"
              )}
            >
              <input
                type="radio"
                name="scenario-calendar-mode"
                className="mt-0.5 accent-brand-600"
                checked={mode === opt.value}
                onChange={() => setMode(opt.value)}
              />
              <span className="text-[13px] font-medium text-text-primary">{opt.label}</span>
              <span className="col-start-2 text-[11px] text-text-secondary">{opt.hint}</span>
            </label>
          ))}
        </fieldset>

        {mode === "custom" ? (
          <div className="space-y-4 border-t border-border-subtle pt-3">
            {/* No cenário, "sem turnos" continua significando contínuo — o modo `always_on` é o
                default de linha nova, e é a única superfície onde a frase antiga segue verdadeira. */}
            <ShiftsEditor
              value={shifts}
              onChange={setShifts}
              disabled={setCalendar.isPending}
              emptyHint="Sem turnos: o cenário opera 24 horas por dia, todos os dias."
            />
            <ExceptionsList
              value={exceptions}
              onChange={setExceptions}
              disabled={setCalendar.isPending}
            />
            {custom.error ? (
              <p
                className="flex items-center gap-1.5 rounded-md bg-rd-danger-50 px-3 py-2 text-[12px] text-rd-danger-600"
                role="alert"
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {custom.error}
              </p>
            ) : !custom.calendar ? (
              <p className="text-[11px] text-text-tertiary">
                Adicione ao menos um turno (ou escolha 24 horas).
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="border-t border-border-subtle px-4 py-3">
        <Button
          type="button"
          onClick={() => void save()}
          disabled={setCalendar.isPending || customInvalid}
        >
          <Save className="mr-1.5 h-4 w-4" /> Aplicar ao cenário
        </Button>
      </div>
    </div>
  );
}
